import React, { useEffect, useState } from "react";
import StudentDashboardLayout from "./StudentDashboardLayout";
import ATKTForm from "../../components/ATKTForm";
import { getAtktStatusAPI, getMyAtktFormAPI, getAtktReceipt } from "../../utils/Api";
import { toast } from "react-toastify";
import PrintableAtktReceipt from "../../components/PrintableAtktReceipt";

const StudentATKTForm = () => {
  const [status, setStatus] = useState(null);
  const [myForm, setMyForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("studentToken")) {
      window.location.href = "/student/login";
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statusRes, formRes] = await Promise.all([
          getAtktStatusAPI(),
          getMyAtktFormAPI().catch(() => null), // Ignore 404
        ]);
        setStatus(statusRes);
        setMyForm(formRes?.form || null);
      } catch (error) {
        toast.error("Failed to load ATKT form status");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <StudentDashboardLayout>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </StudentDashboardLayout>
    );
  }

  if (!status?.enabled) {
    return (
      <StudentDashboardLayout>
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4">A.T.K.T Examination Form</h2>
            <p className="text-gray-600">
              The A.T.K.T examination form is currently not available. Please check back later.
            </p>
          </div>
        </div>
      </StudentDashboardLayout>
    );
  }

  const handleOpenReceipt = async () => {
    if (!myForm?._id) return;
    try {
      setLoadingReceipt(true);
      const resp = await getAtktReceipt(myForm._id);
      if (resp?.success && resp.data) {
        setReceiptData(resp.data);
        setShowReceiptModal(true);
      } else {
        toast.error(resp?.message || "Failed to load ATKT receipt");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not fetch receipt");
    } finally {
      setLoadingReceipt(false);
    }
  };

  if (myForm && myForm.paymentStatus === "paid") {
    // Show read-only submitted form
    return (
      <StudentDashboardLayout>
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4">A.T.K.T Examination Form - Submitted</h2>
            <p className="text-gray-600">
              You have already submitted your A.T.K.T examination form. Below is your submitted information.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                onClick={handleOpenReceipt}
                disabled={loadingReceipt}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
              >
                {loadingReceipt ? "Loading Receipt..." : "View / Print Receipt"}
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Personal Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold mb-4">Personal Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Name
                  </label>
                  <p className="text-gray-900">{myForm.studentName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Roll Number
                  </label>
                  <p className="text-gray-900">{myForm.rollNumber}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Number
                  </label>
                  <p className="text-gray-900">{myForm.contactNumber}</p>
                </div>
              </div>
            </div>

            {/* Academic Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold mb-4">Academic Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course
                  </label>
                  <p className="text-gray-900">{myForm.course}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Batch
                  </label>
                  <p className="text-gray-900">{myForm.batch}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pattern
                  </label>
                  <p className="text-gray-900">{myForm.pattern}</p>
                </div>
              </div>
            </div>

            {/* Subjects */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold mb-4">Selected Subjects</h3>
              <div className="space-y-2">
                {myForm.subjects.filter(subject => subject.type !== "section").map((subject, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md">
                    <span className="text-gray-700">{subject.label}</span>
                    {subject.group === "optional" && (
                      <span className="text-sm text-gray-500">(Optional)</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Submission & Payment Info */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-semibold mb-4">Submission & Payment Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Submitted By</label>
                  <p className="text-gray-900">{myForm.submittedByRole}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Submission Date</label>
                  <p className="text-gray-900">{new Date(myForm.createdAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                  <p className="text-green-600 font-medium">Paid</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount Paid</label>
                  <p className="text-gray-900">₹{(myForm.amount || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {showReceiptModal && (
          <PrintableAtktReceipt
            data={receiptData}
            onClose={() => setShowReceiptModal(false)}
          />
        )}
      </StudentDashboardLayout>
    );
  }

  return (
    <StudentDashboardLayout>
      <ATKTForm portal="student" existingForm={myForm && myForm.paymentStatus !== "paid" ? myForm : null} />
      {showReceiptModal && receiptData && (
        <PrintableAtktReceipt
          data={receiptData}
          onClose={() => setShowReceiptModal(false)}
        />
      )}
    </StudentDashboardLayout>
  );
};

export default StudentATKTForm;
